// ═══════════════════════════════════════════════════════════════
// ProfileEditorScreen.web.tsx: web variant of the editor.
//
// On web we use WebDialog instead of the full-screen slide-up. The
// form fields use HTML inputs (so browser autofill + a11y work) and
// the avatar upload uses the existing pipeline (fromWebFile +
// uploadImageAsset, same as the rest of the app's image uploads).
// ═══════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera } from 'lucide-react';
import { WebDialog } from '@/components/common/WebDialog.web';
import { useTheme } from '@/hooks/useTheme';
import { useLocale } from '@/hooks/useLocale';
import { useProfile, useUpdateProfile } from '@/hooks/useProfileApi';
import { useToast } from '@/hooks/useToast';
// Existing pipeline: no new endpoint needed.
// fromWebFile: turns a browser File into the shared UploadableImageAsset shape
// uploadImageAsset: signs an URL via POST /api/media/images/upload-url then PUTs the blob,
//   returns the public fileUrl. See apps/mobile/src/lib/image-upload.ts.
import { fromWebFile, uploadImageAsset } from '@/lib/image-upload';

export interface ProfileEditorScreenProps {
  onClose: () => void;
}

export function ProfileEditorScreen({ onClose }: ProfileEditorScreenProps) {
  const { theme } = useTheme();
  const { isRTL } = useLocale();
  const { t } = useTranslation('common');
  const toast = useToast();
  const profileQuery = useProfile();
  const updateProfile = useUpdateProfile();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [headline, setHeadline] = useState('');
  const [summary, setSummary] = useState('');
  const [location, setLocation] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (profileQuery.data) {
      setName(profileQuery.data.name ?? '');
      setHeadline(profileQuery.data.headline ?? '');
      setSummary(profileQuery.data.summary ?? '');
      setLocation(profileQuery.data.location ?? '');
    }
  }, [profileQuery.data]);

  const handlePickImage = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingPhoto(true);
      const asset = fromWebFile(file); // validates size + mime
      const url = await uploadImageAsset(asset, {
        kind: 'profile-avatar',
        fileName: asset.name,
        mimeType: asset.type,
      });
      await updateProfile.mutateAsync({ avatarUrl: url });
      toast.success(t('profile.photoUpdatedTitle'), t('profile.photoUpdatedBody'));
    } catch (err) {
      toast.error(
        t('profile.photoFailedTitle'),
        err instanceof Error ? err.message : '',
      );
    } finally {
      setUploadingPhoto(false);
      // Reset so the same file can be re-selected next time
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync({ name, headline, summary, location });
      toast.success(t('profile.savedTitle'), t('profile.savedBody'));
      onClose();
    } catch (err) {
      toast.error(
        t('profile.saveFailedTitle'),
        err instanceof Error ? err.message : '',
      );
    }
  };

  const avatarUri = profileQuery.data?.avatarUrl ?? undefined;

  return (
    <WebDialog
      title={t('profile.editTitle')}
      onClose={onClose}
      maxWidth={520}
      footer={
        <button
          onClick={handleSave}
          disabled={updateProfile.isPending}
          style={primaryBtn(theme, updateProfile.isPending)}
        >
          {updateProfile.isPending ? t('profile.savingLabel') : t('actions.save')}
        </button>
      }
    >
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Avatar: clickable */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 6px' }}>
          <div style={{ position: 'relative', width: 110, height: 110 }}>
            {avatarUri ? (
              <img
                src={avatarUri}
                alt=""
                style={{ width: 110, height: 110, borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <div
                style={{
                  width: 110, height: 110, borderRadius: '50%',
                  background: theme.surfaceSecondary,
                }}
              />
            )}
            <button
              onClick={handlePickImage}
              disabled={uploadingPhoto}
              aria-label={t('actions.edit')}
              style={{
                position: 'absolute',
                bottom: 0, right: 0,
                width: 34, height: 34,
                borderRadius: '50%',
                border: `3px solid ${theme.background}`,
                background: theme.buttonPrimaryBg,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: uploadingPhoto ? 'wait' : 'pointer',
                opacity: uploadingPhoto ? 0.6 : 1,
              }}
            >
              <Camera size={14} strokeWidth={2} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        <Field label={t('profile.fields.name')} value={name} onChange={setName} theme={theme} />
        <Field label={t('profile.fields.headline')} value={headline} onChange={setHeadline} theme={theme} />
        <Field label={t('profile.fields.summary')} value={summary} onChange={setSummary} theme={theme} multiline />
        <Field label={t('profile.fields.location')} value={location} onChange={setLocation} theme={theme} />
      </div>
    </WebDialog>
  );
}

function Field({
  label,
  value,
  onChange,
  multiline,
  theme,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  theme: any;
}) {
  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: theme.surface,
    border: `1px solid ${theme.borderSubtle}`,
    borderRadius: 10,
    color: theme.text,
    fontSize: 14.5,
    padding: '10px 12px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };
  return (
    <label style={{ display: 'block' }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          color: theme.textTertiary,
          marginBottom: 6,
          paddingInlineStart: 2,
        }}
      >
        {label}
      </div>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          style={{ ...inputStyle, resize: 'vertical', minHeight: 88 }}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      )}
    </label>
  );
}

function primaryBtn(theme: any, disabled: boolean): React.CSSProperties {
  return {
    width: '100%',
    height: 42,
    border: 0,
    borderRadius: 10,
    background: disabled ? theme.surfaceSecondary : theme.buttonPrimaryBg,
    color: disabled ? theme.textTertiary : '#fff',
    fontSize: 14.5,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
